import { RecommendPriceResponseSchema } from '@/lib/service/ecs/contracts';
import type { RecommendPriceResponse } from '@/lib/service/types';
import { devError } from '@/lib/utils/devLog';

/** Non-user-facing marker — UI should show translated `servicePrices.requestFailed`. */
export const RECOMMEND_PRICE_GENERIC_FAILURE = 'RECOMMEND_PRICE_GENERIC_FAILURE';

export interface RecommendPriceQuery {
  countryCode: string;
  locale: string;
  watchReference?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readErrorMessage(json: unknown): string | null {
  if (!isRecord(json) || !Array.isArray(json.errors)) {
    return null;
  }

  const first = json.errors[0];
  if (isRecord(first) && typeof first.message === 'string' && first.message) {
    return first.message;
  }

  return null;
}

export function buildRecommendPriceUrl(query: RecommendPriceQuery): string {
  const params = new URLSearchParams({
    countryCode: query.countryCode,
    locale: query.locale,
    watchReference: query.watchReference?.trim() ?? '',
  });

  return `/api/service/recommend-price?${params.toString()}`;
}

export async function fetchRecommendPrice(
  query: RecommendPriceQuery
): Promise<RecommendPriceResponse> {
  let response: Response;
  try {
    response = await fetch(buildRecommendPriceUrl(query));
  } catch (error) {
    devError('[recommendPriceClient] request failed:', error);
    throw new Error(RECOMMEND_PRICE_GENERIC_FAILURE, { cause: error });
  }

  const json: unknown = await response.json().catch(() => null);

  if (!response.ok || !isRecord(json) || json.success !== true) {
    throw new Error(readErrorMessage(json) ?? RECOMMEND_PRICE_GENERIC_FAILURE);
  }

  const parsed = RecommendPriceResponseSchema.safeParse(json.data ?? {});
  if (!parsed.success) {
    throw new Error(RECOMMEND_PRICE_GENERIC_FAILURE);
  }

  return parsed.data;
}
